import { useCallback, useEffect, useRef } from 'react';

import { CancelError, CancellablePromise } from '@atlassian/search-client/cancellable-promise';
import { fetchJiraQuickFindDetails } from '@atlassian/search-client/jira-quick-find-details';
import { getEdgeRegionHint } from '@atlassian/search-common/get-edge-region-hint';

import type { BootstrapState } from '../../../common/constants/bootstrap';
import { serveQfJiraUnhydratedResults } from '../../../common/utils/experiments/serve-qf-jira-unhydrated-results';
import {
	collectJiraQuickFindDetailIds,
	markJiraAvatarsLoading,
	mergeJiraQuickFindDetails,
} from './jira-quick-find-details';
import type { SearchResults } from './types';

/** Quick Find only: publish immediately, then apply display details for the Jira treatment. */
export const useJiraQuickFindDetails = (
	{
		primaryProduct,
		aggAbsoluteUrl,
		productDataRegions,
	}: Pick<BootstrapState, 'primaryProduct' | 'aggAbsoluteUrl' | 'productDataRegions'>,
	setSearchResults: (results: SearchResults) => void,
	logException: (error: unknown) => void,
): {
	publishResults: (source: SearchResults) => Promise<void>;
	cancelDetails: () => void;
} => {
	// This guard requires both the Jira host product and the experiment, not just Jira result types.
	const enabled = serveQfJiraUnhydratedResults(primaryProduct);
	const mountedRef = useRef(false);
	const requestRef = useRef<
		CancellablePromise<Awaited<ReturnType<typeof fetchJiraQuickFindDetails>>> | undefined
	>(undefined);
	const cancelDetails = useCallback(() => requestRef.current?.cancel(), []);

	// Lifecycle cleanup only: fetching is explicitly triggered by publishResults below.
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			cancelDetails();
		};
	}, [cancelDetails, enabled]);

	const publishResults = useCallback(
		async (source: SearchResults) => {
			// An initial search may finish after Quick Find unmounts.
			if (!mountedRef.current) {
				return;
			}
			cancelDetails();
			const ids = enabled ? collectJiraQuickFindDetailIds(source) : undefined;
			if (!ids) {
				setSearchResults(source);
				return;
			}

			setSearchResults(markJiraAvatarsLoading(source));
			const request = new CancellablePromise(
				fetchJiraQuickFindDetails(ids, {
					aggAbsoluteUrl,
					edgeRegionHint:
						primaryProduct && productDataRegions
							? getEdgeRegionHint(primaryProduct, productDataRegions)
							: undefined,
				}),
			);
			requestRef.current = request;
			try {
				const { data, errors } = await request.promise();
				setSearchResults(mergeJiraQuickFindDetails(source, data));
				if (errors?.length) {
					logException(new Error(errors.map((error) => error.message).join('; ')));
				}
			} catch (error) {
				if (!(error instanceof CancelError)) {
					setSearchResults(source);
					logException(error);
				}
			}
		},
		[
			aggAbsoluteUrl,
			cancelDetails,
			enabled,
			logException,
			primaryProduct,
			productDataRegions,
			setSearchResults,
		],
	);

	return { publishResults, cancelDetails };
};
